import React, { useCallback, useRef } from 'react';
import { AiCreateMediaPlanButton } from '@/features/ai/media-plans/ai-create-media-plan-button';
import { useFlagEnabled } from '@/utils/feature-flags';
import {
  CellMouseOutEvent,
  CellMouseOverEvent,
  ColDef,
  NewValueParams
} from '@ag-grid-community/core';
import { AgGridReact, CustomCellRendererProps } from '@ag-grid-community/react';
import { defaultColDef, formatCurrency } from '../../components/tables/table-utils';
import '@ag-grid-community/styles/ag-grid.css';
import '@ag-grid-community/styles/ag-theme-quartz.css';
import { useStore } from '../../store/store';
import {
  DeliveredUnitsRenderer,
  HeaderComponent,
  MarginRenderer,
  MediaSpendRenderer,
  NameRenderer,
  PlatformBuyNameRenderer,
  PlatformBuyTooltipRenderer,
  PlatformEntityRenderer,
  ViewButtonRenderer
} from './media-buys-table-cell-components';
import { getUnitName } from 'shared/src/domain/line-item/line-item-utils';
import { useUrlTableState } from '../../components/tables/table-state-persistence';
import { useChannelRatesContext } from '@/features/strategy/channel-rates-context';
import { UnitPriceType, unitPriceTypeById } from 'shared/src/domain/line-item/line-item-channels';
import { MediaPlanApiUpdateRequest } from 'shared/src/api/media-plan-api-types';
import { MediaPlan } from 'shared/src/domain/media-buy/media-buy-types';
import { MediaPlansActions } from '@/features/media-plans/media-plans-action';
import { useLineItemContext } from '@/features/line-items/line-items-context';
import { trpc } from '@/utils/trpc-client';
import { generateMediaPlansTotalRow } from '@/features/media-plans/media-plans-table-utils';
import { ExecutionLineItemTrpcResponse } from 'shared/src/trpc-types/line-item-trpc-types';
import { AddMediaPlanButton } from '@/features/media-plans/add-media-plan-button';
import { adjustForCPMById } from '@/features/line-items/line-item-form/utils/line-item-form-utils';
import type { ISODateRange } from 'shared/src/date-string-utils/types';
import { useCubeQuery } from '@/components/display/use-cube-query';

export type MediaPlanTableMetrics = {
  actual_delivered_units: number;
  actual_media_spend: number;
  current_margin: number;
  unit_cost_incurred: number;
  target_margin: number;
};

export type MediaPlanTableData = MediaPlan & {
  metrics: MediaPlanTableMetrics | undefined;
  isTotal: boolean;
};

type Props = {
  lineItem: ExecutionLineItemTrpcResponse;
  dateRange: ISODateRange;
};

export function MediaPlansTableContainer({ lineItem, dateRange }: Props) {
  const { channels } = useChannelRatesContext();
  const { mediaPlans } = useLineItemContext();
  const unitPriceType = unitPriceTypeById(channels, lineItem.unit_price_type_id);
  const prevTableDataRef = useRef<MediaPlanTableData[]>([]);

  const result = useCubeQuery({
    dimensions: ['media_buy_metrics_daily.media_plan_id', 'line_items.target_margin'],
    measures: [
      'media_buy_metrics_daily.actual_units',
      'media_buy_metrics_daily.actual_spend',
      'media_buy_metrics_daily.unit_cost_incurred',
      'media_buy_metrics_daily.current_margin'
    ],
    timeDimensions: [
      {
        dimension: 'media_buy_metrics_daily.day',
        dateRange: [dateRange.from, dateRange.to]
      }
    ],
    filters: [
      {
        member: 'media_buy_metrics_daily.line_item_id',
        operator: 'equals',
        values: [lineItem.id]
      }
    ]
  });

  const tableData =
    result.status === 'success'
      ? buildTableData(mediaPlans, result.rows)
      : prevTableDataRef.current;

  if (result.status === 'success') {
    prevTableDataRef.current = tableData;
  }

  return (
    <MediaPlansTableV2
      lineItem={lineItem}
      unitPriceType={unitPriceType}
      tableData={tableData}
      colorMap={{}}
    />
  );
}

type TableProps = {
  lineItem: ExecutionLineItemTrpcResponse;
  unitPriceType: UnitPriceType;
  tableData: MediaPlanTableData[];
  colorMap: Record<string, string>;
};

export function MediaPlansTableV2({ lineItem, unitPriceType, tableData, colorMap }: TableProps) {
  const setHighlightedGraph = useStore(state => state.setHighlightedGraph);
  const { initialState, storeGridState } = useUrlTableState();
  const { mutateAsync, isPending } = trpc.updateMediaPlan.useMutation();
  const utils = trpc.useUtils();
  const assistMeEnabled = useFlagEnabled('assist-me');

  async function updateMediaPlan(media_plan_id: string, update: MediaPlanApiUpdateRequest) {
    if (isPending) return;
    const { strategy_id, id: line_item_id } = lineItem;
    await mutateAsync({ strategy_id, line_item_id, media_plan_id, media_plan: update });
    await utils.invalidate();
  }

  const onCellMouseOut = useCallback(
    (e: CellMouseOutEvent<MediaPlanTableData>) => {
      if (!e.event || !e.data) return;
      if (!e.node.isHovered()) setHighlightedGraph(null);
    },
    [setHighlightedGraph]
  );

  const onCellMouseOver = useCallback(
    (e: CellMouseOverEvent<MediaPlanTableData>) => {
      if (!e.event || !e.data) return;
      if (e.node.isHovered()) {
        setHighlightedGraph(e.data.media_buy?.id);
      }
    },
    [setHighlightedGraph]
  );

  const onMouseLeave = useCallback(() => {
    setTimeout(() => setHighlightedGraph(null), 500);
  }, [setHighlightedGraph]);

  const totalsRow = generateMediaPlansTotalRow(lineItem, tableData);

  return (
    <div>
      <div className="flex h-[50px] items-center justify-between">
        <MediaPlanCount length={tableData.length} />
        <div className="flex items-center gap-2">
          {assistMeEnabled ? (
            <AiCreateMediaPlanButton
              executionLineItemId={lineItem.id}
              strategyId={lineItem.strategy_id}
              lineItemName={lineItem.name}
              lineItemPlannedUnitCost={lineItem.planned_unit_cost}
              lineItemUnitPriceTypeName={lineItem.unit_price_type_name}
              lineItemGeo={lineItem.geo}
              lineItemTacticName={lineItem.tactic_name}
              lineItemChannelName={lineItem.channel_name}
              mediaPlatforms={lineItem.media_platforms}
            />
          ) : null}
          <AddMediaPlanButton lineItem={lineItem} />
        </div>
      </div>
      <div className="flex h-full w-full flex-col">
        <div
          className="ag-theme-quartz"
          style={{
            height: '100%',
            width: '100%'
          }}
          onMouseLeave={onMouseLeave}>
          <AgGridReact
            gridOptions={{ headerHeight: 36, rowHeight: 42 }}
            rowData={tableData}
            columnDefs={getColumnDefs(
              lineItem,
              unitPriceType,
              colorMap,
              hasMetrics(tableData),
              updateMediaPlan
            )}
            defaultColDef={defaultColDef}
            domLayout="autoHeight"
            getRowStyle={({ data }) => ({
              ...(data.isTotal ? { background: '#f0f0f0' } : {})
            })}
            pinnedBottomRowData={[totalsRow]}
            onCellMouseOver={onCellMouseOver}
            onCellMouseOut={onCellMouseOut}
            initialState={initialState}
            onStateUpdated={storeGridState}
            tooltipShowDelay={0}
          />
        </div>
      </div>
    </div>
  );
}

function MediaPlanCount(props: { length: number }) {
  return (
    <div className="item-center flex gap-4">
      <div className="text-lg font-bold">Media Plans</div>
      <div className="rounded bg-blue-100 px-2 py-0.5 font-semibold text-gray-600">
        {props.length}
      </div>
    </div>
  );
}

function getColumnDefs(
  lineItem: ExecutionLineItemTrpcResponse,
  unitPriceType: UnitPriceType,
  colorMap: Record<string, string>,
  showMetrics: boolean,
  updateMediaPlan: (mediaPlanId: string, update: MediaPlanApiUpdateRequest) => void
): ColDef<MediaPlanTableData>[] {
  const unitPriceTypeAbbr = getUnitName(unitPriceType.name).abbr;
  return [
    {
      cellRendererParams: { colorMap },
      cellRenderer: ViewButtonRenderer,
      headerName: '',
      cellStyle: { padding: 0 },
      maxWidth: 70,
      pinned: 'left',
      resizable: false,
      suppressMovable: true,
      suppressAutoSize: true
    },
    {
      field: 'name',
      headerName: 'Plan Name',
      editable: true,
      onCellValueChanged: (update: NewValueParams<MediaPlanTableData, string>) => {
        const { oldValue, newValue, data } = update;
        if (newValue !== oldValue) updateMediaPlan(data.id, { name: newValue || undefined });
      },
      maxWidth: 700,
      width: 300,
      pinned: 'left',
      cellRenderer: NameRenderer
    },
    {
      field: 'media_buy.media_platform_id',
      headerName: 'Platform Entity',
      cellRenderer: PlatformEntityRenderer
    },
    {
      field: 'media_buy.name',
      headerName: 'Platform Buy Name',
      cellRenderer: PlatformBuyNameRenderer,
      width: 300,
      tooltipField: 'media_buy.name',
      tooltipComponent: PlatformBuyTooltipRenderer
    },
    {
      field: 'metrics.current_margin',
      headerName: 'Plan Margin (actual)',
      hide: !showMetrics,
      cellRenderer: MarginRenderer
    },
    {
      field: 'metrics.actual_delivered_units',
      headerName: `${unitPriceTypeAbbr} Delivered`,
      headerComponent: HeaderComponent,
      cellDataType: 'number',
      cellRenderer: DeliveredUnitsRenderer,
      cellStyle: () => numericalCellStyle,
      valueFormatter: params =>
        Math.round(params.data?.metrics?.actual_delivered_units || 0).toLocaleString()
    },
    {
      field: 'metrics.actual_media_spend',
      headerName: 'Media Spend',
      cellDataType: 'number',
      cellRenderer: MediaSpendRenderer,
      cellStyle: () => numericalCellStyle
    },
    {
      field: 'metrics.unit_cost_incurred',
      headerName: `${unitPriceType.name} Cost`,
      hide: !showMetrics,
      cellDataType: 'number',
      cellStyle: () => numericalCellStyle,
      valueFormatter: params => {
        const value = params.data?.metrics?.unit_cost_incurred;
        if (value == null) return '';
        return formatCurrency(adjustForCPMById(value, lineItem.unit_price_type_id));
      }
    },
    {
      field: 'media_buy.budget',
      headerName: 'Media Buy Budget',
      hide: !showMetrics,
      cellDataType: 'number',
      cellStyle: () => numericalCellStyle,
      valueFormatter: params => {
        const value = params.data?.media_buy?.budget;
        return value != null ? formatCurrency(value) : '';
      }
    },
    {
      cellRenderer: ({ data }: CustomCellRendererProps<MediaPlanTableData>) =>
        data && !data.isTotal && <MediaPlansActions data={data} lineItem={lineItem} />,
      cellStyle: { padding: 0, border: 'none' },
      width: 64,
      headerName: '',
      resizable: false,
      pinned: 'right',
      suppressHeaderFilterButton: true,
      suppressHeaderContextMenu: true,
      suppressMovable: true
    }
  ];
}

const numericalCellStyle = { textAlign: 'right' };

export function hasMetrics(tableData: MediaPlanTableData[]) {
  return tableData.some(td => td.metrics && td.metrics.actual_delivered_units > 0);
}

type CubeMetricsRow = {
  'media_buy_metrics_daily.media_plan_id': string;
  'line_items.target_margin': number;
  'media_buy_metrics_daily.actual_units': number;
  'media_buy_metrics_daily.actual_spend': number;
  'media_buy_metrics_daily.unit_cost_incurred': number;
  'media_buy_metrics_daily.current_margin': number;
};

function buildTableData(mediaPlans: MediaPlan[], cubeRows: CubeMetricsRow[]): MediaPlanTableData[] {
  const metricsMap = new Map<string, MediaPlanTableMetrics>();
  for (const row of cubeRows) {
    metricsMap.set(row['media_buy_metrics_daily.media_plan_id'], {
      actual_delivered_units: Number(row['media_buy_metrics_daily.actual_units']) || 0,
      actual_media_spend: Number(row['media_buy_metrics_daily.actual_spend']) || 0,
      unit_cost_incurred: Number(row['media_buy_metrics_daily.unit_cost_incurred']) || 0,
      current_margin: Number(row['media_buy_metrics_daily.current_margin']) || 0,
      target_margin: Number(row['line_items.target_margin']) || 0
    });
  }
  return mediaPlans.map(mp => ({
    ...mp,
    metrics: metricsMap.get(mp.id),
    isTotal: false
  }));
}
